import { useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import { internalLayerVersionsKeys } from 'hooks/internalLayerVersion/queryKeys';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import { ApprovalStatus } from '../../../../../types/approvals';
import { fetchInternalLayerVersions } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';

const STATUS_PRIORITY: Record<ApprovalStatus, number> = {
  APPROVED: 0,
  PENDING: 1,
  DRAFT: 2,
};

/**
 * Use the following criteria to pick default version for a layer: APPROVED
 * (highest version #) -> PENDING -> DRAFT
 */
function pickDefaultVersion(
  versions: InternalLayerVersion[]
): InternalLayerVersion | null {
  if (!versions.length) return null;

  return versions.reduce((best, current) => {
    const currentStatusPriority =
      STATUS_PRIORITY[current.approval?.approvalStatus ?? 'DRAFT'];
    const bestStatusPriority =
      STATUS_PRIORITY[best.approval?.approvalStatus ?? 'DRAFT'];

    // If statuses are different, prefer lower priority number (APPROVED over PENDING, etc)
    if (currentStatusPriority !== bestStatusPriority) {
      return currentStatusPriority < bestStatusPriority ? current : best;
    }

    // If statuses are the same, prefer higher version number
    return current.versionNumber > best.versionNumber ? current : best;
  }, versions[0]);
}

/**
 * When internal layers and their versions have finished loading, initializes
 * selectedInternalLayerVersionByInternalLayerId so each layer has a default
 * selection: latest APPROVED version takes priority. Only sets keys that are
 * not already present so user selections are preserved.
 */
export function useDefaultInternalLayerVersionSelection({
  aorFilterIds,
  setSelectedInternalLayerVersionByInternalLayerId,
}: {
  aorFilterIds: string[] | null;
  setSelectedInternalLayerVersionByInternalLayerId: React.Dispatch<
    React.SetStateAction<Record<string, InternalLayerVersion>>
  >;
}) {
  const groupGuid = useGroupGuid();

  const { data } = useListInternalLayersQuery({
    filter: {
      groupGuid: groupGuid ?? '',
      ...(aorFilterIds?.length ? { aorIds: aorFilterIds } : {}),
    },
  });

  const layers = useMemo(() => data?.data ?? [], [data]);

  const versionQueries = useQueries({
    queries: layers.map((layer) => ({
      queryKey: internalLayerVersionsKeys.list({
        filter: { internalLayerId: layer.id },
      }),
      queryFn: async () => {
        return fetchInternalLayerVersions({
          filter: { internalLayerId: layer.id },
        });
      },
    })),
  });

  useEffect(() => {
    // Check if all queries have data
    const allFinished =
      versionQueries.length > 0 && versionQueries.every((q) => q.isSuccess);
    if (!allFinished) return;

    setSelectedInternalLayerVersionByInternalLayerId((prev) => {
      const next = { ...prev };
      let changed = false;

      layers.forEach((layer, i) => {
        const versions = versionQueries[i].data?.data;
        // Only set if not already present AND a version exists
        if (next[layer.id] === undefined && versions?.length) {
          const defaultVersion = pickDefaultVersion(versions);
          if (defaultVersion) {
            next[layer.id] = defaultVersion;
            changed = true;
          }
        }
      });

      return changed ? next : prev;
    });
  }, [
    versionQueries,
    layers,
    setSelectedInternalLayerVersionByInternalLayerId,
  ]);
}
